import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { BorderAnimation } from "../../components/Brand/BorderAnimation";

interface YaasPreviewBalanceCardProps {
    /** Pre-formatted USD value, e.g. "$12,450.75". The connected-no-
     *  yaas / disconnected / apply-below-min surfaces feed this from
     *  `previewPosition.position.formattedUSDvValue`. */
    formatted: string;
    /** Eyebrow line above the number. Defaults to "Available
     *  Positions"; the disconnected preview overrides to "Holding"
     *  (or whatever the consumer maps `previewBalanceLabel` to). */
    eyebrow?: string;
    /** Optional ember-edge entrance animation overlay. Mirrors
     *  YaasPositionCard's trace API so this card can drop into
     *  YaasEarnProjectionCTA's existing entrance choreography
     *  without rewiring. */
    traceBorderIn?: boolean;
    traceBorderOut?: boolean;
    traceTriggerKey?: number;
    traceTopSpeed?: number;
    traceSideSpeed?: number;
    traceBottomSpeed?: number;
    /** The balance could not be read. The number and its unit are
     *  dropped and `notice` stands in their place, so a failed read
     *  never shows as a figure. Always pass a `notice` with it. */
    unavailable?: boolean;
    /** One line under the number: a load failure, or a qualifier on a
     *  partial balance. The card keeps its frame either way, so the
     *  surface does not jump between loading, failed, and loaded. */
    notice?: React.ReactNode;
}

/**
 * Preview-only balance card for the connected-no-yaas / disconnected
 * / apply-below-min surfaces. Restores the bordered chrome — accent-
 * top stripe, line-base sides + bottom, `p-3 md:p-6` padding — that
 * `YaasPositionCard` shed when the active state went flat. Reads as
 * a ghost "this is what your position WOULD look like" framing.
 *
 * Layout: eyebrow on top, big number + `USDv` suffix below (baseline-
 * aligned). No IndicationUnit dot — the entrance border trace
 * already signals the focal area, and the dot would read as
 * redundant chrome in this ghost-card context.
 *
 * Mechanism-only / SDK-free. Hoisted into `@solomon-apps/shared`
 * (widgets/yaas) so the sim-mode Enable Rewards preview frame renders
 * SDK-free. apps/main re-exports it from its old path via a shim.
 */
export function YaasPreviewBalanceCard({
    formatted,
    eyebrow = "Available Positions",
    traceBorderIn = false,
    traceBorderOut = false,
    traceTriggerKey = 0,
    traceTopSpeed,
    traceSideSpeed,
    traceBottomSpeed,
    unavailable = false,
    notice
}: YaasPreviewBalanceCardProps): React.ReactElement {
    return (
        <div className="relative border md:border-2 border-line-base border-t-accent-base p-3 md:p-6 flex flex-col gap-2">
            {(traceBorderIn || traceBorderOut) && (
                <BorderAnimation
                    direction={traceBorderOut ? "empty" : "fill"}
                    color="accent"
                    triggerKey={traceTriggerKey}
                    topSpeed={traceTopSpeed}
                    sideSpeed={traceSideSpeed}
                    bottomSpeed={traceBottomSpeed}
                />
            )}
            <Text
                variant="small"
                render={<span />}
                className="text-foreground-muted"
            >
                {eyebrow}
            </Text>
            {/* An unavailable value is replaced by its notice rather
                than a placeholder glyph: a failed read never shows as
                something shaped like a figure. */}
            {!unavailable && (
                <div className="flex items-baseline gap-2">
                    <Text
                        variant="number-lg"
                        className="text-foreground-base tabular-nums"
                    >
                        {formatted}
                    </Text>
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        USDv
                    </Text>
                </div>
            )}
            {notice}
        </div>
    );
}
